/** Staking-plan arithmetic shared by the Stake, Swap and Dashboard pages (mirrors StakingManager). */
export const EPOCHS_PER_YEAR = 730; // 12-hour epochs

/** Nominal APR of a per-epoch rate (the client's figures: 50% … 365%). */
export function aprOf(ratePerEpoch: number): number {
  return ratePerEpoch * EPOCHS_PER_YEAR;
}

/** Yearly yield with the rate compounded every epoch. */
export function apyOf(ratePerEpoch: number): number {
  return Math.pow(1 + ratePerEpoch, EPOCHS_PER_YEAR) - 1;
}

/**
 * What 1 SYN staked becomes after `epochs` payouts: the plan's rate while locked, then (timed plans) the no-lock
 * rate, exactly as a lock that has ended keeps compounding in the contract. `lockEpochs` null: the plan's own rate
 * throughout (no lock, permanent).
 */
export function planMultiple(rate: number, noLockRate: number, lockEpochs: number | null, epochs: number): number {
  if (lockEpochs === null || epochs <= lockEpochs) return Math.pow(1 + rate, epochs);
  return Math.pow(1 + rate, lockEpochs) * Math.pow(1 + noLockRate, epochs - lockEpochs);
}

/** Deposit fee and what is staked, as StakingManager._stake computes it (the fee rounds down). */
export function stakePreview(amount: bigint, feeBps: bigint, price: bigint | undefined) {
  const fee = (amount * feeBps) / 10_000n;
  const net = amount - fee;
  return { fee, net, usd: price ? (net * price) / 10n ** 18n : undefined };
}

/**
 * What each plan will actually pay per epoch once the staking mint budget is short: StakingManager._processEpoch
 * mints min(wanted, budget), where wanted = Σ plan value × rate, and scales every plan by the same factor. Below the
 * budget every plan pays its full rate.
 */
export function effectiveRates(plans: readonly { rate: bigint; totalValue: bigint }[] | undefined, budget: bigint | undefined) {
  const list = plans ?? [];
  const wanted = list.reduce((sum, p) => sum + (p.totalValue * p.rate) / 10n ** 18n, 0n);
  const capped = budget !== undefined && list.length > 0 && wanted > budget;
  return {
    wanted,
    capped,
    /** The share of its rate every plan pays: 1 below the budget. */
    factor: capped ? Number((budget * 1_000_000n) / wanted) / 1_000_000 : 1,
    rates: list.map((p) => (capped ? (p.rate * budget) / wanted : p.rate)),
  };
}

/** What a withdrawal of `gross` pays after the exit fee (StakingManager._payOut). */
export function afterExitFee(gross: bigint, feeBps: bigint): bigint {
  return gross - (gross * feeBps) / 10_000n;
}
